import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { HiChevronLeft } from "react-icons/hi";
import { SessionTemplate } from "../../types/post";
import { sessionService } from "../../services/sessionService";
import { asDisplayNameOrNull } from "../../utils/displayName";
import SearchableSelect from "../common/SearchableSelect";

interface StartSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionCreated?: (sessionId: string) => void;
  initialTemplate?: (SessionTemplate & { id: string }) | null;
  mode?: "start" | "completed";
}

const StartSessionModal: React.FC<StartSessionModalProps> = ({
  isOpen,
  onClose,
  onSessionCreated,
  initialTemplate,
  mode = "start",
}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // State
  const [sessionTemplates, setSessionTemplates] = useState<
    (SessionTemplate & { id: string })[]
  >([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Get current date for the session
  const now = new Date();
  const currentDate = now.toISOString().split("T")[0]; // YYYY-MM-DD format

  // Fetch session templates
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!currentUser?.uid || !isOpen) return;

      try {
        setLoadingTemplates(true);
        const templates = await sessionService.getAllSessionTemplates(
          currentUser.uid,
        );
        setSessionTemplates(templates);
      } catch (error) {
        console.error("Error fetching templates:", error);
        setError("Failed to load templates");
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [currentUser?.uid, isOpen]);

  // Initialize from initialTemplate prop
  useEffect(() => {
    if (initialTemplate) {
      setSelectedTemplateId(initialTemplate.id);
    }
  }, [initialTemplate]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedTemplateId("");
      setError(null);
    }
  }, [isOpen]);

  const isCompleted = mode === "completed";

  const handleStartFromScratch = async () => {
    if (!currentUser?.uid) {
      setError("You must be logged in to start a session");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const sessionData: any = {
        ActivityMeasurementList: [],
        description: "",
        plannedDate: currentDate,
        status: isCompleted ? ("complete" as const) : ("executing" as const),
      };

      if (!isCompleted) {
        sessionData.startTime = new Date().toISOString();
        sessionData.startedAdHoc = true;
      }

      const sessionId = await sessionService.createSession(
        currentUser.uid,
        sessionData,
      );

      if (onSessionCreated) {
        onSessionCreated(sessionId);
      }

      navigate(`/session/${sessionId}`);
      onClose();
    } catch (error) {
      console.error("Error starting blank session:", error);
      setError("Failed to create session. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartFromTemplate = async () => {
    if (!currentUser?.uid) {
      setError("You must be logged in to start a session");
      return;
    }

    if (!selectedTemplateId) {
      setError("Please select a template");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get the selected template
      const template = sessionTemplates.find(
        (t) => t.id === selectedTemplateId,
      );
      if (!template) {
        setError("Template not found");
        return;
      }

      const sessionData: any = {
        ActivityMeasurementList: template.ActivityMeasurementList || [],
        description: template.description || "",
        plannedDate: currentDate,
        status: isCompleted ? ("complete" as const) : ("executing" as const),
      };

      if (!isCompleted) {
        sessionData.startTime = new Date().toISOString();
        sessionData.startedAdHoc = true;
      }

      // Add optional fields from template
      const safeTemplateName = asDisplayNameOrNull(template.name);
      if (safeTemplateName) {
        sessionData.name = safeTemplateName;
      }
      if (template.trainingTypeID?.trim()) {
        sessionData.trainingTypeID = template.trainingTypeID.trim();
      }
      if (template.sessionTypeID?.trim()) {
        sessionData.sessionTypeID = template.sessionTypeID.trim();
      }

      const sessionId = await sessionService.createSession(
        currentUser.uid,
        sessionData,
      );

      if (onSessionCreated) {
        onSessionCreated(sessionId);
      }

      // Navigate to the session
      navigate(`/session/${sessionId}`);
      onClose();
    } catch (error) {
      console.error("Error starting session from template:", error);
      setError("Failed to start session. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-75 backdrop-blur-sm overflow-hidden">
      <div className="flex flex-col h-full md:items-center md:justify-center md:overflow-auto">
        <div
          className="w-full max-w-lg mx-auto bg-base-100 md:rounded-xl shadow-lg flex flex-col h-full md:h-auto md:max-h-[70vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Fixed on mobile */}
          <div className="flex justify-between items-center p-4 md:p-6 flex-shrink-0 bg-base-100 md:rounded-t-xl">
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                disabled={loading}
                className="p-1 rounded-full hover:bg-base-300 bg-base-300 transition-colors disabled:opacity-50"
                style={{ touchAction: "manipulation" }}
              >
                <HiChevronLeft className="h-8 w-8 text-base-content/70" />
              </button>
              <div>
                <h2 className="text-xl text-base-content font-grotesk">
                  {isCompleted ? (
                    <>
                      Add <strong>Completed Session</strong>
                    </>
                  ) : (
                    <>
                      Start <strong>Session</strong>
                    </>
                  )}
                </h2>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-8 pt-0">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Template Selection */}
            <div className="mb-6">
              <h3 className="text-sm font-grotesk font-semibold text-base-content/80 mb-3">
                Use a Template
              </h3>

              {loadingTemplates ? (
                <div className="text-center py-8 text-base-content/60">
                  Loading templates...
                </div>
              ) : sessionTemplates.length === 0 ? (
                <div className="text-center py-8 text-base-content/60 bg-base-100 rounded-lg border-2 border-dashed border-base-200">
                  <p className="text-sm">No templates available</p>
                  <p className="text-xs mt-1">
                    Create templates in the Planner
                  </p>
                </div>
              ) : (
                <SearchableSelect
                  options={sessionTemplates
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((template) => ({
                      value: template.id,
                      label: `${template.name}${
                        template.ActivityMeasurementList?.length
                          ? ` (${template.ActivityMeasurementList.length} sets)`
                          : ""
                      }`,
                    }))}
                  value={selectedTemplateId}
                  onChange={(value) => setSelectedTemplateId(value)}
                  placeholder="Search session templates..."
                  allOptionLabel="Select a session template..."
                  emptyValue=""
                  disabled={loading}
                  inputClassName={`bg-base-200 !text-base !font-semibold ${selectedTemplateId ? "!border-primary !border-2" : ""}`}
                />
              )}
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-base-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-base-100 text-base-content/60">
                  OR
                </span>
              </div>
            </div>

            {/* Start from Scratch Section */}
            <div>
              <button
                onClick={handleStartFromScratch}
                disabled={loading || loadingTemplates}
                className="w-full px-4 py-4 bg-base-200 border-2 border-base-200 rounded-lg hover:bg-base-300 hover:border-base-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
              >
                <p className="font-grotesk font-semibold text-base-content">
                  Start from Scratch
                </p>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div
            className="p-8 flex-shrink-0 relative z-50 flex items-center gap-3"
            style={{
              pointerEvents: "auto",
              touchAction: "manipulation",
            }}
          >
            <button
              onClick={handleStartFromTemplate}
              disabled={loading || !selectedTemplateId}
              className="bg-primary text-white py-4 px-6 font-grotesk font-bold flex-1 rounded-xl disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed"
              style={{ touchAction: "manipulation" }}
            >
              {loading
                ? "Creating..."
                : isCompleted
                  ? "Add Session"
                  : "Start Session"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartSessionModal;
